import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Download, Lock, Unlock, Power } from 'lucide-react';
import { exportCsv } from '../../utils/exportCsv';
import Button from '../../components/common/Button';
import Card from '../../components/common/Card';
import SearchInput from '../../components/common/SearchInput';
import Select from '../../components/common/Select';
import StatusBadge from '../../components/common/StatusBadge';
import Badge from '../../components/common/Badge';
import DataTable from '../../components/common/DataTable';
import EmptyState from '../../components/common/EmptyState';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { Store } from 'lucide-react';
import { adminService } from '../../services/adminService';
import { useToast } from '../../contexts/ToastContext';
import { formatBusinessType, formatPlanType } from '../../utils/enums';

const PAGE_SIZE = 20;

const AdminStores = () => {
  const navigate = useNavigate();
  const { error, success } = useToast();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState({
    status: '', businessType: '', planType: '', isBlocked: '', q: '', includeInactive: false
  });
  const [loading, setLoading] = useState(true);
  const [pendingAction, setPendingAction] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminService.listStores({ ...filter, page, limit: PAGE_SIZE });
      setRows(data?.rows || []);
      setTotal(data?.count || 0);
    } catch (e) {
      error(e.message);
    } finally {
      setLoading(false);
    }
  }, [filter, page, error]);

  useEffect(() => {
    load();
  }, [load]);

  const runAction = async () => {
    if (!pendingAction) return;
    const { type, store } = pendingAction;
    try {
      if (type === 'block') await adminService.blockStore(store.id);
      else if (type === 'unblock') await adminService.unblockStore(store.id);
      else if (type === 'deactivate') await adminService.deactivateStore(store.id);
      else if (type === 'activate') await adminService.activateStore(store.id);
      success('Loja atualizada');
      setPendingAction(null);
      load();
    } catch (e) {
      error(e.message);
    }
  };

  const clearFilters = () => {
    setFilter({ status: '', businessType: '', planType: '', isBlocked: '', q: '', includeInactive: false });
    setPage(1);
  };

  const columns = [
    {
      key: 'name',
      header: 'Loja',
      render: (s) => (
        <div>
          <p className="font-semibold text-app-text">{s.name}</p>
          <p className="text-xs text-app-text-secondary">/{s.slug}</p>
        </div>
      ),
    },
    {
      key: 'owner',
      header: 'MANAGER',
      render: (s) => (
        <div className="text-xs">
          <p className="text-app-text">{s.owner?.name || '—'}</p>
          <p className="text-app-text-secondary">{s.owner?.email || ''}</p>
        </div>
      ),
    },
    {
      key: 'businessType',
      header: 'Modelo',
      render: (s) => (
        <Badge color="neutral" variant="soft" size="sm">
          {formatBusinessType(s.businessType)}
        </Badge>
      ),
    },
    {
      key: 'planType',
      header: 'Plano',
      render: (s) => (
        <Badge color={s.planType === 'CUSTOM' ? 'info' : 'neutral'} variant="soft" size="sm">
          {formatPlanType(s.planType || 'DEFAULT')}
        </Badge>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (s) => (
        <div className="flex flex-col gap-1">
          {s.deletedAt && <Badge color="neutral" variant="soft" size="sm">Desativada</Badge>}
          {s.isBlocked && <Badge color="warning" variant="soft" size="sm">Bloqueada</Badge>}
          {!s.deletedAt && !s.isBlocked && (
            <StatusBadge domain="store" status={s.status} />
          )}
        </div>
      ),
    },
    {
      key: 'domain',
      header: 'Domínio',
      render: (s) =>
        s.customDomain ? (
          <div className="text-xs">
            <span className="font-mono">{s.customDomain}</span>
            {!s.customDomainVerified && (
              <Badge color="warning" size="sm" variant="soft" className="ml-2">
                Não verificado
              </Badge>
            )}
          </div>
        ) : (
          <span className="text-xs text-app-text-secondary">—</span>
        ),
    },
    {
      key: 'actions',
      header: 'Ações',
      render: (s) => (
        <div className="flex gap-1 justify-end" onClick={(e) => e.stopPropagation()}>
          <Button
            size="sm"
            variant="subtle"
            onClick={() => navigate(`/admin-reviter/lojas/${s.id}`)}
          >
            Detalhe
          </Button>
          {s.deletedAt ? (
            <Button
              size="sm"
              variant="subtle"
              leftIcon={<Power size={14} />}
              onClick={() => setPendingAction({ type: 'activate', store: s })}
            >
              Reativar
            </Button>
          ) : (
            <>
              {s.isBlocked ? (
                <Button
                  size="sm"
                  variant="subtle"
                  leftIcon={<Unlock size={14} />}
                  onClick={() => setPendingAction({ type: 'unblock', store: s })}
                >
                  Desbloquear
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="warning"
                  leftIcon={<Lock size={14} />}
                  onClick={() => setPendingAction({ type: 'block', store: s })}
                >
                  Bloquear
                </Button>
              )}
              <Button
                size="sm"
                variant="danger"
                leftIcon={<Power size={14} />}
                onClick={() => setPendingAction({ type: 'deactivate', store: s })}
              >
                Desativar
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-3xl font-extrabold text-app-text">Lojas</h1>
        <div className="flex gap-2">
          <Button
            variant="subtle"
            leftIcon={<Download size={16} />}
            onClick={() =>
              exportCsv({
                filename: 'lojas.csv',
                columns: [
                  { key: 'id', header: 'ID' },
                  { key: 'name', header: 'Nome' },
                  { key: 'slug', header: 'Slug' },
                  { key: 'businessType', header: 'Modelo' },
                  { key: 'planType', header: 'Plano' },
                  { key: 'status', header: 'Status' },
                  { key: 'isBlocked', header: 'Bloqueada', format: (v) => (v ? 'Sim' : 'Não') },
                  { key: 'customDomain', header: 'Domínio' },
                  { key: 'customDomainVerified', header: 'Verificado', format: (v) => (v ? 'Sim' : 'Não') },
                  { header: 'MANAGER', format: (_, r) => r.owner?.name || '' },
                  { header: 'Email MANAGER', format: (_, r) => r.owner?.email || '' },
                ],
                rows,
              })
            }
            disabled={!rows.length}
          >
            Exportar CSV
          </Button>
          <Link to="/admin-reviter/lojas/nova">
            <Button leftIcon={<Plus size={16} />}>Nova loja</Button>
          </Link>
        </div>
      </div>

      <Card className="p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div className="md:col-span-2">
            <SearchInput
              value={filter.q}
              onChange={(q) => {
                setFilter((f) => ({ ...f, q }));
                setPage(1);
              }}
              placeholder="Buscar nome, slug ou domínio…"
              debounceMs={300}
            />
          </div>
          <Select
            value={filter.status}
            onChange={(e) => {
              setFilter((f) => ({ ...f, status: e.target.value }));
              setPage(1);
            }}
            options={[
              { value: '', label: 'Todos status' },
              { value: 'active', label: 'Ativa' },
              { value: 'suspended', label: 'Suspensa' },
              { value: 'trial', label: 'Trial' },
            ]}
          />
          <Select
            value={filter.businessType}
            onChange={(e) => {
              setFilter((f) => ({ ...f, businessType: e.target.value }));
              setPage(1);
            }}
            options={[
              { value: '', label: 'Todos modelos' },
              { value: 'seller', label: 'Venda direta' },
              { value: 'reseller', label: 'Com revendedores' },
              { value: 'catalog', label: 'Catálogo (WhatsApp)' },
            ]}
          />
          <Select
            value={filter.planType}
            onChange={(e) => {
              setFilter((f) => ({ ...f, planType: e.target.value }));
              setPage(1);
            }}
            options={[
              { value: '', label: 'Todos planos' },
              { value: 'DEFAULT', label: 'Padrão' },
              { value: 'CUSTOM', label: 'Customizado' },
            ]}
          />
          <Select
            value={filter.isBlocked}
            onChange={(e) => {
              setFilter((f) => ({ ...f, isBlocked: e.target.value }));
              setPage(1);
            }}
            options={[
              { value: '', label: 'Bloqueio: todos' },
              { value: 'true', label: 'Bloqueadas' },
              { value: 'false', label: 'Não bloqueadas' },
            ]}
          />
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={filter.includeInactive}
              onChange={(e) => {
                setFilter((f) => ({ ...f, includeInactive: e.target.checked }));
                setPage(1);
              }}
            />
            Incluir desativadas
          </label>
        </div>
        {(filter.q || filter.status || filter.businessType || filter.planType || filter.isBlocked || filter.includeInactive) && (
          <div className="mt-3">
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              Limpar filtros
            </Button>
          </div>
        )}
      </Card>

      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        total={total}
        page={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        onRowClick={(s) => navigate(`/admin-reviter/lojas/${s.id}`)}
        empty={
          <EmptyState
            icon={Store}
            title="Nenhuma loja encontrada"
            description="Ajuste filtros ou cadastre uma nova loja."
            action={
              <Link to="/admin-reviter/lojas/nova">
                <Button>Nova loja</Button>
              </Link>
            }
          />
        }
      />

      <ConfirmDialog
        isOpen={!!pendingAction}
        onClose={() => setPendingAction(null)}
        onConfirm={runAction}
        title={
          pendingAction?.type === 'block' ? 'Bloquear loja?' :
          pendingAction?.type === 'unblock' ? 'Desbloquear loja?' :
          pendingAction?.type === 'deactivate' ? 'Desativar loja?' :
          'Reativar loja?'
        }
        message={
          pendingAction?.type === 'block'
            ? 'A loja continuará existindo mas o whitelabel exibirá tela de "indisponível". O MANAGER ainda terá acesso ao backoffice.'
          : pendingAction?.type === 'unblock'
            ? 'A loja voltará a ficar acessível pelo whitelabel.'
          : pendingAction?.type === 'deactivate'
            ? 'A loja deixará de ser resolvida. Todas as URLs públicas retornarão 404. Pode ser revertido a qualquer momento.'
          : 'A loja será restaurada e voltará a aparecer publicamente.'
        }
        confirmLabel={
          pendingAction?.type === 'block' ? 'Bloquear' :
          pendingAction?.type === 'unblock' ? 'Desbloquear' :
          pendingAction?.type === 'deactivate' ? 'Desativar' :
          'Reativar'
        }
        variant={pendingAction?.type === 'deactivate' || pendingAction?.type === 'block' ? 'danger' : 'accent'}
      />
    </div>
  );
};

export default AdminStores;
